<template>
  <div
    class="project-story dealtracker-story project-case-study"
    :style="{ '--case-chrome': chromeHeight + 'px' }"
  >
    <div class="case-hero case-hero-text">
      <ProjectPageHeader project-id="dealtracker" context="Personal project">
        <h1>Dealtracker</h1>
        <p class="project-subtitle">
          Outdoor clothing sale alerts matched to my preferences
        </p>
      </ProjectPageHeader>
      <div class="case-hero-lede">
        <p class="lede">
          During my exchange in Melbourne, I noticed that outdoor brands I
          liked, including Arc’teryx and Patagonia, were often cheaper than in
          Hong Kong and frequently discounted. Finding the right item still
          meant checking long sale pages before an offer ended. Dealtracker
          automates those checks and emails me when it finds a new match for my
          saved preferences.
        </p>
      </div>
    </div>
    <ProjectChapterNav
      ref="chapterNav"
      :chapters="chapters"
      :active-chapter="activeChapter"
      :progress="chapterProgress"
      @navigate="goToChapter"
    />
    <article class="project-narrative case-narrative">
      <section id="case-chapter-0" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>01</span>Preferences</span>
          <h2
            id="from-noisy-sale-pages-to-a-short-inbox-digest"
            data-source-anchor="from-noisy-sale-pages-to-a-short-inbox-digest"
          >
            A shortlist in my inbox
          </h2>
        </header>
        <div class="case-pair">
          <div class="case-copy" data-case-reveal>
            <p>
              I set filters for brands, sizes, gender, and minimum discounts. A scheduled job checks selected stores against those
              filters, turning their sale listings into a shortlist. The
              configuration shown here covers two Australian retailers and seven
              brands.
            </p>
          </div>
          <div class="case-stage" data-case-reveal>
            <div class="case-stage-content">
              <div class="deal-stat-grid">
                <div class="deal-stat">
                  <strong>2</strong>
                  <span>Australian stores</span>
                </div>
                <div class="deal-stat">
                  <strong>7</strong>
                  <span>brands I track</span>
                </div>
                <div class="deal-stat">
                  <strong>29-39%</strong>
                  <span>minimum discount</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="case-chapter-1" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>02</span>The digest</span>
          <h2 id="the-email-output" data-source-anchor="the-email-output">
            Enough information to decide
          </h2>
        </header>
        <div class="case-pair">
          <div class="case-copy" data-case-reveal>
            <p>
              A useful alert lets me assess the offer before opening the store’s
              page. I designed the digest to group items by brand and sort them by discount,
              showing a photograph and the original and sale prices beside each
              product link. The examples below show how that information comes
              together in the email.
            </p>
          </div>
          <div class="case-stage" data-case-reveal>
            <div class="case-stage-content">
              <div class="digest-pair">
                <figure>
                  <ExpandableProjectImage
                    src="/assets/img/dealtracker_arcteryx_email.png"
                    alt="Dealtracker email for Arc'teryx and The North Face deals"
                    title="Arc'teryx and The North Face digest"
                  />
                  <figcaption>Arc'teryx and The North Face digest</figcaption>
                </figure>
                <figure>
                  <ExpandableProjectImage
                    src="/assets/img/dealtracker_patagonia_email.png"
                    alt="Dealtracker email for Patagonia and Icebreaker deals"
                    title="Patagonia and Icebreaker digest"
                  />
                  <figcaption>Patagonia and Icebreaker digest</figcaption>
                </figure>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="case-chapter-2" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>03</span>Collection</span>
          <h2
            id="what-happens-behind-the-email"
            data-source-anchor="what-happens-behind-the-email"
          >
            Reading the stores' product data
          </h2>
        </header>
        <div class="case-pair">
          <div class="case-copy" data-case-reveal>
            <p>
              WildEarth and Find Your Feet express similar products and filters
              differently. Applying my preferences consistently meant first
              accounting for each store’s URL format, page loading, and product
              labels. I kept the store-specific handling at the point of
              collection, then normalised the listings so the same filtering and
              email logic could work across both retailers. The stages below
              follow a listing into the digest.
            </p>
          </div>
          <div class="case-stage" data-case-reveal>
            <div class="case-stage-content">
              <div class="pipeline">
                <div class="pipeline-row">
                  <span class="pipeline-label">Store filters</span>
                  <p>
                    Saved brand, size, gender, and discount preferences are
                    encoded in the URL format each store expects.
                  </p>
                </div>
                <div class="pipeline-row">
                  <span class="pipeline-label">Page loading</span>
                  <p>
                    A headless browser runs the page’s scripts so it can read
                    product grids loaded after the initial response.
                  </p>
                </div>
                <div class="pipeline-row">
                  <span class="pipeline-label">Product data</span>
                  <p>
                    Arc'teryx and Arcteryx count as the same brand. When a
                    store's gender labels are inconsistent, the product title is
                    used to infer the category.
                  </p>
                </div>
                <div class="pipeline-row">
                  <span class="pipeline-label">New matches</span>
                  <p>
                    Each qualifying product is checked against the saved history
                    before it can enter the digest.
                  </p>
                </div>
                <div class="pipeline-row">
                  <span class="pipeline-label">Delivery</span>
                  <p>
                    An HTML email is sent when at least one new product passes
                    all filters.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section id="case-chapter-3" class="case-section" data-case-chapter>
        <header class="case-section-heading" data-case-reveal>
          <span class="case-eyebrow"><span>04</span>History</span>
          <h2 id="the-control-layer" data-source-anchor="the-control-layer">
            Remembering what I have already seen
          </h2>
        </header>
        <div class="case-reading case-copy" data-case-reveal>
          <p>
            A product can stay on sale across many scheduled checks. Without a
            record of earlier results, a successful search would keep filling my
            inbox with the same items. I added a history file so the job could
            distinguish a new match from one it had already reported. A run with
            nothing new finishes quietly.
          </p>
          <p>
            Sometimes I want to see the current selection again, including items
            I previously passed over. The job also checks its inbox for a reset
            command that clears the saved history. I can restart the shortlist
            through the same email workflow in which I receive the deals.
          </p>
          <p class="project-resource-links">
            <GitHubRepoLink
              href="https://github.com/henryhyw/dealtracker"
              label="Source on GitHub"
            />
          </p>
        </div>
      </section>
    </article>
  </div>
</template>
<script>
import GitHubRepoLink from "./ui/GitHubRepoLink.vue";
import ProjectPageHeader from "./ui/ProjectPageHeader.vue";
import ExpandableProjectImage from "./ui/ExpandableProjectImage.vue";
import projectCaseStudyMixin from "../mixins/projectCaseStudyMixin";
import ProjectChapterNav from "./ui/ProjectChapterNav.vue";

export default {
  computed: {
    chapters() {
      return [
        { id: "case-chapter-0", label: "Preferences" },
        { id: "case-chapter-1", label: "The digest" },
        { id: "case-chapter-2", label: "Collection" },
        { id: "case-chapter-3", label: "History" },
      ];
    },
  },
  name: "DealtrackerPage",
  components: { ProjectChapterNav, GitHubRepoLink, ProjectPageHeader, ExpandableProjectImage },
  mixins: [projectCaseStudyMixin],
};
</script>
<style scoped>
.project-story {
  --ink: #2f2f2f;
  --muted: #767676;
  --line: #e2e2e2;
  --wash: #f7f7f7;
  --accent: var(--site-text-primary);
}

.lede {
  font-size: 1.08em;
  color: var(--ink);
}

.deal-hero {
  margin: 1.7rem 0 2.2rem;
  padding: 18px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: linear-gradient(
    180deg,
    rgba(247, 247, 247, 0.96),
    rgba(255, 255, 255, 0.72)
  );
}

.eyebrow {
  display: block;
  margin-bottom: 0.35rem;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.72rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--muted);
}

.deal-hero h2 {
  margin: 0 0 0.7rem;
  font-size: 1.55rem;
  line-height: 1.2;
}

.deal-hero p {
  margin: 0;
  color: #5f5f5f;
}

.deal-stat-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-top: 18px;
}

.deal-stat {
  border-top: 2px solid var(--accent);
  padding-top: 10px;
}

.deal-stat strong {
  display: block;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 1.35rem;
  color: var(--ink);
}

.deal-stat span {
  display: block;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.72rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--muted);
}

.digest-pair {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin: 1.1rem 0 0.4rem;
  align-items: start;
}

.digest-pair figure {
  margin: 0;
}

.digest-pair img {
  width: auto;
  max-width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  box-shadow: none;
  background: #fff;
}

.digest-pair figcaption {
  margin-top: 0.55rem;
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.78rem;
  color: var(--muted);
  text-align: center;
}

.pipeline {
  margin: 1.2rem 0 0.5rem;
  border-top: 1px solid var(--line);
}

.pipeline-row {
  display: grid;
  grid-template-columns: 105px 1fr;
  gap: 16px;
  padding: 13px 0;
  border-bottom: 1px solid var(--line);
}

.pipeline-label {
  font-family: "Quattrocento Sans", sans-serif;
  font-size: 0.8rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--accent);
}

.pipeline-row p {
  margin: 0;
  color: var(--muted);
}

.feature-list {
  margin: 1rem 0 1.3rem;
  border-top: 1px solid var(--line);
}

.feature-row {
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 18px;
  padding: 14px 0;
  border-bottom: 1px solid var(--line);
}

.feature-row span {
  font-family: "Quattrocento Sans", sans-serif;
  font-weight: 700;
  color: var(--ink);
}

.feature-row p {
  margin: 0;
  color: var(--muted);
}

body.dark-mode .project-story {
  --ink: #d7d7d7;
  --muted: #a1a1a1;
  --line: #2e2e2e;
  --accent: var(--site-text-primary);
}

body.dark-mode .deal-hero {
  border-color: #2c2c2c;
  background: linear-gradient(
    180deg,
    rgba(var(--site-accent-shade-rgb), 0.88),
    rgba(15, 15, 15, 0.74)
  );
}

body.dark-mode .deal-hero p,
body.dark-mode .pipeline-row p,
body.dark-mode .feature-row p,
body.dark-mode .digest-pair figcaption {
  color: #a1a1a1;
}

body.dark-mode .digest-pair img {
  box-shadow: none;
}

@media (max-width: 700px) {
  .digest-pair {
    grid-template-columns: 1fr;
  }

  .deal-stat-grid {
    grid-template-columns: 1fr;
  }

  .pipeline-row,
  .feature-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }
}

@media (max-width: 420px) {
  .deal-hero {
    padding: 14px;
  }

  .deal-hero h2 {
    font-size: 1.35rem;
  }
}
</style>
